import {TanStackTable} from "../../Components/Tables/TanStackTable";
import {BaseCard} from "../../Components/Cards/BaseCard";
import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../Utils/StringHelper";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import {TextInput} from "../../Components/Form/TextInput";
import {SecondaryButton} from "../../Components/Buttons/SecondaryButton";
import {useBoundStore} from "../../Store/store";
import {useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";

export const MyContracts = () => {
    const {t} = useTranslation();
    const contracts = useBoundStore((state) => state.contracts);
    const contractsMetaData = useBoundStore((state) => state.contractsMetaData);
    const getMyContracts = useBoundStore((state) => state.getAllMyContracts);
    const [statusFilter, setStatusFilter] = useState([]);
    const [forceNewData, setForceNewData] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);

    const handleFilterClick = (status) => {
        if(statusFilter.includes(status)) {
            setStatusFilter(statusFilter.filter(s => s !== status))
        } else {
            setStatusFilter([...statusFilter, status])
        }
    }

    useEffect(() => {
        setForceNewData(!forceNewData);
    }, [contracts]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    useEffect(() => {
        getMyContracts(statusFilter, currentPage)
    }, [getMyContracts, statusFilter, currentPage]);

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('branch', {
            header: t('tables.customer'),
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`)
        }),
        columnHelper.accessor('branch', {
            header: t('tables.contact'),
            id: 'contact_person',
            cell: props => truncateString(`${props.cell.getValue().contact_firstname} ${props.cell.getValue().contact_lastname}`)
        }),
        columnHelper.accessor('id', {
            header: t('contracts.contract_number'),
            cell: props => truncateString(props.cell.getValue()),
        }),
        columnHelper.accessor('created_at', {
            header: t('tables.created_at'),
            cell: props => {
                return dayjs.unix(props.cell.getValue().timestamp).format('DD/MM/YYYY HH:mm')
            },
        }),
        columnHelper.accessor('document', {
            header: t('invoices.document'),
            cell: props => {
                if((props.cell.getValue() !== null && props.cell.getValue() !== undefined) && props.cell.getValue().hasOwnProperty('url') ) {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon icon={faDownload} className={'text-primary cursor-pointer'} onClick={() => window.open(props.cell.getValue().url, '_blank')}/>
                        </div>
                    )
                } else {
                    return (<p>-</p>)
                }
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.status'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <div>
                            {
                                ['pending'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('contracts.status_to_sign')}</div>
                                )
                            }

                            {
                                ['sent'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-yellow-300 text-yellow-900 rounded-full text-xs opacity-70'}>{t('contracts.status_sent')}</div>
                                )
                            }

                            {
                                ['completed'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('contracts.status_signed')}</div>
                                )
                            }

                            {
                                ['cancelled'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70'}>{t('contracts.status_cancelled')}</div>
                                )
                            }

                        </div>
                    </div>
                )
            },
        })
    ]

    const statusBadge = (status) => {
        const map = {
            pending: {cls: 'bg-orange-300 text-orange-900', label: t('contracts.status_to_sign')},
            sent: {cls: 'bg-yellow-300 text-yellow-900', label: t('contracts.status_sent')},
            completed: {cls: 'bg-green-300 text-green-900', label: t('contracts.status_signed')},
            cancelled: {cls: 'bg-red-300 text-red-900', label: t('contracts.status_cancelled')},
        };
        const info = map[status];
        if (!info) return null;
        return <span className={`px-2 py-1 rounded-full text-xs ${info.cls}`}>{info.label}</span>;
    };

    return (
        <div className="px-4 md:px-0">
            <div className={'mb-2'}>
                <div className={'flex justify-between'}>
                    <div className={'my-auto w-full md:w-1/3'}>
                        <TextInput
                            type={'text'}
                            name={'search'}
                            translationIndex={null}
                            placeholder={t('general.actions.search')}
                            onChange={(e) => console.log(e.target.value)}
                        >
                        </TextInput>
                    </div>
                    <div className={'hidden md:grid grid-cols-3 gap-4 my-auto'}>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('pending')} onClick={() => handleFilterClick('pending')}>
                                {t('contracts.status_to_sign')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('sent')} onClick={() => handleFilterClick('sent')}>
                                {t('contracts.status_sent')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('completed')} onClick={() => handleFilterClick('completed')}>
                                {t('contracts.status_signed')}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
                <div className={'grid md:hidden grid-cols-3 gap-2 mt-2'}>
                    <SecondaryButton outlined={!statusFilter.includes('pending')} onClick={() => handleFilterClick('pending')}>
                        {t('contracts.status_to_sign')}
                    </SecondaryButton>
                    <SecondaryButton outlined={!statusFilter.includes('sent')} onClick={() => handleFilterClick('sent')}>
                        {t('contracts.status_sent')}
                    </SecondaryButton>
                    <SecondaryButton outlined={!statusFilter.includes('completed')} onClick={() => handleFilterClick('completed')}>
                        {t('contracts.status_signed')}
                    </SecondaryButton>
                </div>
            </div>

            {/* Desktop: tabel */}
            <div className="hidden md:block">
                <BaseCard>
                    {contracts?.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-8">{t('contracts.no_results')}</p>
                    ) : (
                        <TanStackTable
                            columns={columns}
                            tableData={contracts}
                            forceNewData={forceNewData}
                            laravelPaginationMetaData={contractsMetaData}
                            onChangePage={(page) => {
                                setCurrentPage(page +1)
                            }}
                        />
                    )}
                </BaseCard>
            </div>

            {/* Mobiel: cards */}
            <div className="md:hidden space-y-3">
                {contracts?.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">{t('contracts.no_results')}</p>
                )}
                {contracts?.map((contract) => (
                    <BaseCard key={contract.id}>
                        <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-gray-900 truncate">{contract.branch?.company_name ?? '-'}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {contract.branch?.contact_firstname} {contract.branch?.contact_lastname}
                                </p>
                            </div>
                            {statusBadge(contract.status)}
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs text-gray-500">
                                #{contract.id} &middot; {dayjs.unix(contract.created_at?.timestamp).format('DD/MM/YYYY')}
                            </div>
                            {contract.document?.url && (
                                <button
                                    onClick={() => window.open(contract.document.url, '_blank')}
                                    className="flex items-center gap-1 text-primary text-xs font-medium"
                                >
                                    <FontAwesomeIcon icon={faDownload} className="text-xs"/>
                                    {t('invoices.document')}
                                </button>
                            )}
                        </div>
                    </BaseCard>
                ))}
                {contractsMetaData?.last_page > 1 && (
                    <div className="flex justify-center gap-2 py-2">
                        {Array.from({length: contractsMetaData.last_page}, (_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`px-3 py-1 rounded text-sm ${currentPage === i + 1 ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'}`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}